import { useCallback, useEffect, useState } from "react";
import { ApiError, get, type Me, type Trip } from "./lib/api.ts";
import { onAuthChanged, signIn, signOut, startSession } from "./lib/session.ts";
import { Link, navigate, tripPath, useRoute } from "./lib/router.tsx";
import { CreateTripPage } from "./pages/CreateTripPage.tsx";
import { JoinPage } from "./pages/JoinPage.tsx";
import { FirstRunGate } from "./pages/PhoneSetupPage.tsx";
import { TripPage } from "./pages/TripPage.tsx";
import { TripsPage } from "./pages/TripsPage.tsx";
import { canReach, Shell } from "./shell/Shell.tsx";
import { Banner, EmptyState, ToastProvider } from "./ui/index.ts";

function useTrip(tripId: string | null) {
  const [trip, setTrip] = useState<Trip | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const refresh = useCallback(async () => {
    if (!tripId) return;
    try {
      setTrip(await get<Trip>(`/v1/trips/${tripId}`));
      setError(null);
    } catch (e) {
      setError(e as Error);
    }
  }, [tripId]);
  useEffect(() => {
    setTrip(null);
    setError(null);
    void refresh();
  }, [refresh]);
  return { trip: trip && trip.id === tripId ? trip : null, error, refresh };
}

export function App() {
  const route = useRoute();
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<"loading" | "anonymous" | "ready" | "error">("loading");
  const tripId = route.name === "trip" ? route.tripId : null;
  const { trip, error: tripError, refresh } = useTrip(tripId);

  // An editor's shell has three sections, and a page or two they are linked to from those. A link
  // to anything else — an old bookmark, or the default landing of a path without a section —
  // would leave them on a page their nav cannot get back to. Replaced rather than pushed, so Back
  // does not walk into it again.
  const section = route.name === "trip" ? route.section : null;
  useEffect(() => {
    if (!trip || !section) return;
    if (!canReach(trip.roles, section)) {
      navigate(tripPath(trip.id, "map"), { replace: true });
    }
  }, [trip, section]);

  useEffect(() => {
    const start = async () => {
      try {
        if (!(await startSession())) return setState("anonymous");
        setMe(await get<Me>("/v1/me"));
        setState("ready");
      } catch (e) {
        console.error("session start failed", e);
        setState("error");
      }
    };
    void start();
    // In the Android shell sign-in finishes natively; it tells the page when to look again.
    return onAuthChanged(() => {
      setState("loading");
      void start();
    });
  }, []);

  if (state === "loading") return <main className="center muted">Loading…</main>;
  if (state === "error") {
    return (
      <main className="center">
        <Banner
          tone="bad"
          message="We couldn’t reach the server. Check your connection."
          action={{ label: "Retry", onClick: () => window.location.reload() }}
        />
      </main>
    );
  }
  if (state === "anonymous" || !me) {
    return (
      <main className="center hero">
        <h1>Trip Media</h1>
        <p>
          Footage from every traveler, verified and ready for editing — while the trip is still
          happening.
        </p>
        <div>
          <button className="button primary" type="button" onClick={signIn}>
            Sign in
          </button>
        </div>
      </main>
    );
  }

  let page;
  if (route.name === "trips") page = <TripsPage me={me} />;
  else if (route.name === "newTrip") page = <CreateTripPage />;
  else if (route.name === "invite") page = <JoinPage token={route.token} />;
  else if (route.name === "trip") {
    if (tripError) {
      page =
        tripError instanceof ApiError && tripError.status === 404 ? (
          <EmptyState title="Trip not found" body="It doesn’t exist, or you are not a member." />
        ) : (
          <Banner
            tone="bad"
            message={tripError.message}
            action={{ label: "Retry", onClick: () => void refresh() }}
          />
        );
    } else if (!trip) page = <p className="muted">Loading…</p>;
    else
      page = (
        <TripPage
          key={trip.id}
          trip={trip}
          section={route.section}
          itemId={route.itemId}
          me={me}
          refresh={refresh}
        />
      );
  } else {
    page = <EmptyState title="Page not found" body={<Link to="/">Back to your trips</Link>} />;
  }

  return (
    <ToastProvider>
      <Shell
        route={route}
        tripName={trip?.name ?? null}
        roles={trip?.roles ?? []}
        userName={me.user.displayName}
        onSignOut={async () => {
          await signOut();
          window.location.assign("/");
        }}
      >
        <FirstRunGate>{page}</FirstRunGate>
      </Shell>
    </ToastProvider>
  );
}
